/**
 * Sélecteur de période de clôture (Fév 2026).
 * Dropdown listant les mois disponibles avec :
 *  - Nom lisible (ex: "Février 2026")
 *  - Solde de clôture
 *  - Statut : Ouvert / Clôturé
 *  - Nombre de transactions
 *
 * Props :
 *   - history: [{ period, closing_balance, tx_count, closure_status }]
 *   - value: period sélectionnée (yyyy-mm)
 *   - onChange(period)
 *   - loading, disabled
 */
import React, { useState, useMemo } from 'react';
import { Calendar, Lock, Unlock, ChevronDown, X } from 'lucide-react';

const MONTHS_FR_LONG = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const readable = (p) => {
  if (!p) return '';
  const [y, m] = p.split('-');
  return `${MONTHS_FR_LONG[+m - 1] || m} ${y}`;
};
const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('fr-FR').format(Math.round(n)));

export default function PeriodClosureSelector({
  history = [], value, onChange, loading, disabled, allowClear = true,
}) {
  const [open, setOpen] = useState(false);

  const sortedDesc = useMemo(
    () => [...(history || [])].sort((a, b) => (a.period < b.period ? 1 : -1)),
    [history]
  );

  const selected = sortedDesc.find(p => p.period === value);

  return (
    <div className="relative" data-testid="period-closure-selector">
      <button
        type="button"
        onClick={() => !disabled && setOpen(v => !v)}
        disabled={disabled || loading}
        className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        data-testid="period-closure-selector-toggle"
      >
        <Calendar className="h-4 w-4 text-slate-500" />
        <span className="font-medium">
          {value ? readable(value) : 'Toutes les périodes'}
        </span>
        {selected?.closure_status === 'closed' && (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full">
            <Lock className="h-2.5 w-2.5" /> Clôturé
          </span>
        )}
        {selected?.closure_status === 'open' && (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
            <Unlock className="h-2.5 w-2.5" /> Ouvert
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-40 max-h-96 overflow-hidden"
             data-testid="period-closure-selector-dropdown">
          <div className="p-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-600">Périodes disponibles ({sortedDesc.length})</span>
            {allowClear && value && (
              <button
                onClick={() => { onChange?.(null); setOpen(false); }}
                className="text-[11px] text-slate-500 hover:text-rose-600 inline-flex items-center gap-1"
                data-testid="period-closure-clear"
              >
                <X className="h-3 w-3" /> Effacer
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
            {sortedDesc.length === 0 && (
              <div className="p-4 text-center text-xs text-slate-400">Aucune période</div>
            )}
            {sortedDesc.map(p => {
              const active = p.period === value;
              return (
                <button
                  key={p.period}
                  onClick={() => { onChange?.(p.period); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors ${active ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : ''}`}
                  data-testid={`period-closure-item-${p.period}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={`text-sm font-medium ${active ? 'text-blue-800' : 'text-slate-800'}`}>
                        {readable(p.period)}
                      </span>
                      {p.closure_status === 'closed' ? (
                        <Lock className="h-3 w-3 text-rose-500" />
                      ) : (
                        <Unlock className="h-3 w-3 text-emerald-500" />
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs font-mono text-slate-700">
                        {fmt(p.closing_balance)} <span className="text-[10px] text-slate-400">XOF</span>
                      </div>
                      <div className="text-[10px] text-slate-400">{p.tx_count || 0} tx</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
